// src/com/bulk_company/BulkCompany.js
import React, { useEffect, useRef, useState } from 'react';
//import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'; // 사용
import StopCircleIcon from '@mui/icons-material/StopCircle'; // 사용중지.
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentHeader, BigContentColumn, ControlBox, CountLabel, ToolBox, HoriContentContainer
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    authTableTreeRepo, bulkCompanyListRepo, bulkCompanyProductsAllListRepo
} from '../../value/AppModel';
import {
    LIST_BULK_COMPANY, SET_USAGE_BULK_COMPANY,
    ADD_BULK_COMPANY, EDIT_BULK_COMPANY, REMOVE_BULK_COMPANY
} from './BulkCompanyGql';
import { BulkCompanyDictionary } from './BulkCompanyDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import PromptDialog from '../../common/PromptDialog';
import BulkProducts from '../bulk_products/BulkProducts';
import { getProductsOfCompany } from '../bulk_company_products/BulkCompanyProductsGql';

const BigColWidth = 620;

/**
 * (BULK_COMPANY) 정보 View Controller
 * Bulk 상품 취급업체 정보 관리 화면
 * 과거 버전에는 이 데이터를 관리하는 화면이 없었음. 기초정보 메뉴에 추가.
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2026-05-13 16:38:05 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function BulkCompany() {
    //const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [companySelected, setCompanySelected] = useState(null);
    const [compEditorState, setCompEditorState] = useState(AppObject.EmptyViewerState);
    const bulkCompanyListData = useReactiveVar(bulkCompanyListRepo);
    const bulkCompanyProductsListData = useReactiveVar(bulkCompanyProductsAllListRepo);
    const [searchCompText, setSearchCompText] = useState('');
    const [countCompToDisplay, setCountCompToDisplay] = useState(0);
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getBulkCompanyList, responseCompList] = useLazyQuery(LIST_BULK_COMPANY, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetCompList(data, option)},
		onError: (error) => addAlert({title: BulkCompanyDictionary.errorTitle.Get, message: [error.message]})
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at con-sole (Uncaught exception) ###
    const [addBulkCompany, responseAddComp] = useMutation( ADD_BULK_COMPANY, {
		onCompleted: (data, option) => onCompleteAddComp(data, option), 
		onError: (error) => addAlert({title: BulkCompanyDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editBulkCompany, responseEditComp] = useMutation( EDIT_BULK_COMPANY, {
		onCompleted: (data, option) => onCompleteEditComp(data, option),
		onError: (error) => addAlert({title: BulkCompanyDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeBulkCompany, responseRemoveComp] = useMutation( REMOVE_BULK_COMPANY, {
		onCompleted: (data, option) => onCompleteRemoveComp(data, option),
		onError: (error) => addAlert({title: BulkCompanyDictionary.errorTitle.Remove, message: [error.message]})
	});
	const [setUsageBulkCompany, responseSetCompUsage] = useMutation(SET_USAGE_BULK_COMPANY, {
		onCompleted: (data, option) => onCompletesetCompUsage(data, option),
		onError: (error) => addAlert({title: BulkCompanyDictionary.errorTitle.SetUsage, message: [error.message]})
	});

    const getBulkCompanyListNow = () => {
        getBulkCompanyList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - bulkCompanyListData.when;
        if(diff > 300) //  || bulkCompanyListData.records.length === 0 - Don't use this condition
            getBulkCompanyListNow();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetCompList = (data, clientOption) => {
        if(data.bulkCompanyList) {
            bulkCompanyListRepo({when:new Date().getTime()/1000, records:data.bulkCompanyList});
        }
    };

    const onCompleteAddComp = (data, clientOption) => {
        if(data.bulkCompanyAdd.ok) {
            setCompEditorState(AppObject.EmptyViewerState);
            getBulkCompanyListNow();
        }
        else addAlert({title:BulkCompanyDictionary.errorTitle.Add, message:[data.bulkCompanyAdd.message]});
    };

    const onCompleteEditComp = (data, clientOption) => {
        if(data.bulkCompanyEdit.ok) {
            setCompEditorState(AppObject.EmptyViewerState);
            getBulkCompanyListNow();
        }
        else addAlert({title:BulkCompanyDictionary.errorTitle.Edit, message:[data.bulkCompanyEdit.message]});
    };

    const onCompleteRemoveComp = (data, clientOption) => {
        if(data.bulkCompanyRemove.ok) {
            getBulkCompanyListNow();
            setCompanySelected(null);
        }
        else addAlert({title:BulkCompanyDictionary.errorTitle.Remove, message:[data.bulkCompanyRemove.message]});
    };

	const onCompletesetCompUsage = (data, option) => {
        if(data.bulkCompanySetUsage.ok) getBulkCompanyListNow();
        else addAlert({title:BulkCompanyDictionary.errorTitle.SetUsage, message:[data.bulkCompanySetUsage.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReloadCompList = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getBulkCompanyListNow();
    };

    const onRequestSaveComp = (item, isEdit) => {
        const mutateParam = {variables:{bulkCompany:item}};
        if(isEdit) editBulkCompany(mutateParam);
        else addBulkCompany(mutateParam);
    };

    // PromptDialog onSubmit callback
    const onSubmitCompName = (newName, isEdit) => {
        setCompEditorState(AppObject.EmptyViewerState);

        if(compEditorState.item) {
            // Edit
            const data = {
                bulkCompanyId: compEditorState.item.bulkCompanyId,
                comName: newName,
            };
            onRequestSaveComp(data, true);
        }
        else onRequestSaveComp({comName: newName}, false);
    };

    const onClickAddComp = () => {
        setCompEditorState({open:true, item:null});
    };

    const onRequestEditComp = (item) => {
        setCompEditorState({open:true, item:{...item}});
    };

    const getProductsMsgOfComp = (item) => {
        const prods = getProductsOfCompany(item.bulkCompanyId, bulkCompanyProductsListData.records);
        if(prods.length === 0) return '해당 업체와 연결된 상품은 없습니다.';
        else return `해당 업체는 ${prods.join(', ')} 상품과 연결되어 있습니다.`;
    };

    const confirmRemoveComp = async (item) => {
        const confirm = await openConfirmDialog({
            title: "Bulk 상품 취급업체 정보 삭제",
            content: [
                `${item.comName} 데이터를 삭제하시겠습니까?`,
                getProductsMsgOfComp(item),
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeBulkCompany({variables:{bulkCompany:{bulkCompanyId:item.bulkCompanyId}}});
    };

    const onChangeSearchCompText = (text) => {
        setSearchCompText(text);
    };

    const onRequestSetCompUsage = (item, useYn) => {
        const param = {
            variables: {
                bulkCompany: {
                    bulkCompanyId: item.bulkCompanyId,
                    useYn: useYn,
                }
            }
        };
        setUsageBulkCompany(param);
    };

    const confirmStopUsing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "업체정보 사용중지",
            content: [
                `${item.comName} 업체 정보를 사용중지하시겠습니까?`,
                getProductsMsgOfComp(item)
            ]
        });

        if(confirm) onRequestSetCompUsage(item, 'N');
    };

    const confirmReusing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "업체정보 사용",
            content: [
                `${item.comName} 업체 정보를 사용하도록 변경하시겠습니까?`,
            ]
        });
        if(confirm) onRequestSetCompUsage(item, 'Y');
    };

    const onClickToolOnCompRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemoveComp(row); break;
            case AppObject.command.EDIT: onRequestEditComp(row); break;
            case AppObject.command.STOP_USING: confirmStopUsing(row); break;
            case AppObject.command.REUSE: confirmReusing(row); break;
            default: return;
        }
    };

    const canAddComp = checkAuth(authTableTree.tree.pr_bulk_company_add);
    const canEditComp = checkAuth(authTableTree.tree.pr_bulk_company_edit);
	const canSetCompUsage = checkAuth(authTableTree.tree.pr_bulk_company_set_usage);
    const tools = canEditComp ? [
        {id:AppObject.command.EDIT, icon:<EditIcon/>, title: '편집'},
        {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning, title: '삭제'},
    ] : [];

    if(canSetCompUsage) {
        tools.push({
            id: AppObject.command.STOP_USING, icon: <StopCircleIcon/>,
            color: AppStyle.mui.error, title: '사용중지',
            where: (row)=>row.useYn === 'Y'
        });
        tools.push({
            id: AppObject.command.REUSE, icon: <PlayCircleFilledWhiteIcon/>,
            color: AppStyle.mui.success, title: '사용',
            where: (row)=>row.useYn === 'N'
        });
    }

    const loadingAnyCompData
        = responseCompList.loading
        || responseAddComp.loading
        || responseEditComp.loading
        || responseRemoveComp.loading
		|| responseSetCompUsage.loading;

    return(
        <HoriContentContainer>
            <BigContentColumn width={BigColWidth}>
                <ContentHeader>
                    <ControlBox>
                        <SearchBox
                            label="Bulk 업체 검색"
                            value={searchCompText}
                            onChangeText={onChangeSearchCompText}
                        />
                    </ControlBox>
                    <ToolBox>
                        <CountLabel count={countCompToDisplay} />
                        {
                            loadingAnyCompData ?
                            <CircularProgress size={20} />
                            :
                            <IconButton
                                color={AppStyle.mui.inherit}
                                onClick={onClickReloadCompList}
                            >
                                <CachedIcon />
                            </IconButton>
                        }
                        {
                            canAddComp &&
                            <IconButton
                                color={AppStyle.mui.primary}
                                style={{marginLeft:10}}
                                onClick={onClickAddComp}
                                disabled={
                                    loadingAnyCompData
                                }
                            >
                                <NoteAddIcon />
                            </IconButton>
                        }
                    </ToolBox>
                </ContentHeader>
                <FlexyTable
                    name={BulkCompanyDictionary.tableProps.tableId}
                    tableProps={BulkCompanyDictionary.tableProps}
                    records={bulkCompanyListData.records}
                    search={searchCompText}
                    tools={tools}
                    onRowClicked={(row) => setCompanySelected(row)}
                    onClickTool={onClickToolOnCompRow}
                    onChangeCountToDisplay={setCountCompToDisplay}
                />
            </BigContentColumn>
            <BigContentColumn style={{flexGrow:1}}>
                <BulkProducts
                    companySelected={companySelected}
                />
            </BigContentColumn>
            <PromptDialog
                open={compEditorState.open}
                title={compEditorState.item ? 'Bulk 상품 취급업체명 수정' : 'Bulk 상품 취급업체 등록'}
                message={"한글 16자 이내로 입력하세요."}
                defaultValue={compEditorState.item ? compEditorState.item.comName : ''}
                isEdit={compEditorState.item ? true : false}
                onSubmit={onSubmitCompName}
                onClose={()=>setCompEditorState(AppObject.EmptyViewerState)}
            />
        </HoriContentContainer>
    );
}
